/**
 * Fiscal ↔ calendar week arithmetic for the range-plan grid's week headers
 * (`rangePlanColumnDefs`) and the cube's weekly columns (`columnsWeekly`). A
 * packed `YYYYWW` is a **calendar year week** everywhere in the frontend — the
 * `year_week` the API takes. Where the calendar is available (the cube's
 * `weekPeriodMapping`), read the key from it instead: the restated 4-5-4
 * boundary drifts a week against the fixed offset below.
 *
 * Split from `yearWeeks.ts` on purpose: that module is ISO-calendar-only
 * (`isoWeeksInYear`, `enumerateYearWeeks`, `getCurrentYearWeek`,
 * `yearWeekToIsoDate`) and tenant-agnostic; everything here hangs off one
 * tenant's fiscal boundary.
 *
 * Boundary truth: Bestseller's financial year starts **August 1st** — fiscal
 * W01 of pair Y1/Y2 is calendar W32 of Y1 per the CALENDAR dim
 * (`fiscal_week_year = 1` at year_week 202632); the pair runs through
 * calendar W31 of Y2. See
 * `specs/domain/range_plan/implementation/investigations.md` (2026-06-09).
 */

/**
 * First calendar week of a fiscal year — the week containing the Aug 1st FY
 * start. Hardcoded: a per-year CALENDAR lookup (the restated 4-5-4 boundary
 * can drift a week across years) is a deferred refinement.
 *
 * Bestseller-specific — this package is tenant-agnostic, but the constant
 * is not; other tenants' fiscal years may start on a different calendar week.
 */
const FISCAL_YEAR_START_CAL_WEEK = 32;

/**
 * Fiscal week number (1..52) of a calendar week: calendar W32 → fiscal W01,
 * …, calendar W31 → fiscal W52. Returns `null` for out-of-range input
 * **and for calendar W53**.
 *
 * W53 has no fiscal address under the 52-week modulo rule: it would fold
 * onto fiscal W22, the same column calendar W01 maps to. Returning `null`
 * makes every caller degrade honestly instead: the main grid labels W53 by
 * its calendar number. Real W53 support needs the per-year CALENDAR lookup
 * deferred alongside the boundary constant above. Which years have a W53
 * at all is `isoWeeksInYear` (`yearWeeks.ts`).
 */
export function fiscalWeekOfCalWeek(calWeek: number): number | null {
  if (!Number.isFinite(calWeek) || calWeek < 1 || calWeek >= 53) return null;
  return ((calWeek - FISCAL_YEAR_START_CAL_WEEK + 52) % 52) + 1;
}

/**
 * Map a packed calendar `YYYYWW` to its `"YYYY1/YYYY2"` fiscal-year pair:
 * calendar weeks ≥ W32 belong to the pair starting that calendar year; weeks
 * ≤ W31 to the pair that started the prior year. Returns `undefined` for
 * missing/malformed input so callers can fall back to insertion order.
 */
export function fiscalPairOfYearWeek(
  yearWeek: number | undefined | null
): string | undefined {
  if (!yearWeek) return undefined;
  const calWeek = yearWeek % 100;
  if (calWeek < 1 || calWeek > 53) return undefined;
  const calYear = Math.floor(yearWeek / 100);
  return calWeek >= FISCAL_YEAR_START_CAL_WEEK
    ? `${calYear}/${calYear + 1}`
    : `${calYear - 1}/${calYear}`;
}
